import type { GoldLayerMetricsResponse } from "~/components/feature/results/components/types/goldLayerMetrics";
import type { UserData } from "~/store";
import { keysToCamelCase, keysToSnakeCase } from "~/utils/caseTransform";
import {
  type FullApiResponseWrapper,
  getAxiosClient,
} from "./api/axios-client";

export interface SendEmailPayload {
  email: string;
  type: "subscription" | "login";
  urlPath?: string;
}

export async function validateUserApi() {
  try {
    const client = getAxiosClient();
    const response = await client.post<FullApiResponseWrapper<UserData>>(
      "/api/v1/users/login_user_access",
      {},
    );
    return keysToCamelCase(response.data.body.results[0]);
  } catch (err: any) {
    console.error("validateUserApi error:", err?.response?.status);
    // Preserve the error structure from axios
    const error = new Error(err.message || "Validation failed");
    (error as any).response = err.response;
    (error as any).status = err.response?.status;
    (error as any).body = err.response?.data;
    throw error;
  }
}

export async function sendEmail({ email, type, urlPath }: SendEmailPayload) {
  try {
    const client = getAxiosClient();

    const body: SendEmailPayload = {
      email,
      type,
    };

    if (urlPath) body.urlPath = urlPath;

    const response = await client.post<
      FullApiResponseWrapper<{ success: boolean }>
    >("/api/v1/users/email/send", keysToSnakeCase(body));
    return response.data;
  } catch (err: any) {
    (err as any).status = err.response?.status;
    (err as any).body = err.response?.data;
    throw err;
  }
}

export async function requestMagicLink(
  email: string,
  urlPath: string,
  type: SendEmailPayload["type"],
) {
  return sendEmail({ email, type, urlPath });
}

export async function createUserApi(userData: any): Promise<UserData> {
  try {
    const client = getAxiosClient();

    const user = {
      email: userData.email,
      first_name: userData.first_name || "",
      second_name: userData.second_name || "",
      surname: userData.first_surname || "",
      second_surname: userData.second_surname || "",
      phone: userData.phone || "",
      second_phone: userData.second_phone || "",
      job_position: userData.job_position || "",
      other_job_position: userData.other_job_position || "",
      external_id: userData.uid,
    };

    const response = await client.post<FullApiResponseWrapper<UserData>>(
      "/api/v1/users/",
      user,
    );
    return keysToCamelCase(response.data.body.results[0]);
  } catch (err: any) {
    throw err;
  }
}
export async function getGoldLayerMetrics(
  companyId: string,
): Promise<GoldLayerMetricsResponse> {
  try {
    const client = getAxiosClient();

    const response = await client.get<{ data: GoldLayerMetricsResponse[] }>(
      `/api/v1/metrics/gold-layer/${companyId}`,
    );

    // Return the first item from the data array
    const data = response.data.data?.[0];
    if (!data) {
      throw new Error("No data returned from metrics API");
    }

    return data;
  } catch (err: any) {
    console.error("getGoldLayerMetrics error:", err?.response?.status);
    const error = new Error(err.message || "Failed to fetch metrics");
    (error as any).response = err.response;
    (error as any).status = err.response?.status;
    (error as any).body = err.response?.data;
    throw error;
  }
}
